import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../styles/settings-menu.css';

/**
 * SettingsMenu - Session-specific actions menu (right three dots)
 * Context actions for current conversation: Clear, Share, Export
 * App-level actions (Settings, Help) are in left hamburger menu
 */
const SettingsMenu = ({
  isOpen,
  onClose,
  anchorElement,
  vertical = 'business',
  onOpenSettings = null,
  onOpenHelp = null,
  onOpenAbout = null,
  onClearChat = null,
  onShareSession = null,
  onExportChat = null
}) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && 
          anchorElement && !anchorElement.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isOpen, onClose, anchorElement]);

  if (!isOpen) return null;

  const isOpsIQ = vertical === 'opsiq' || vertical === 'business';
  const showAppItems = isOpsIQ && (onOpenSettings || onOpenHelp || onOpenAbout);
  const showSessionItems = !!(onClearChat || onShareSession || onExportChat);

  const content = (
    <div
      className="ov-settings-menu"
      ref={menuRef}
      style={{
        position: 'fixed',
        top: anchorElement ? `${anchorElement.getBoundingClientRect().bottom + 6}px` : '60px',
        right: anchorElement ? `${Math.max(8, window.innerWidth - anchorElement.getBoundingClientRect().right)}px` : '16px',
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '0.75rem',
        boxShadow: '0 10px 20px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)',
        padding: '0.5rem',
        minWidth: '180px',
        zIndex: 5000,
      }}
    >
      {showAppItems && (
        <>
          {onOpenSettings && (
            <button
              className="ov-settings-menu-item"
              onClick={() => {
                onOpenSettings();
                onClose();
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#111827',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
              </svg>
              Settings
            </button>
          )}

          {onOpenHelp && (
            <button
              className="ov-settings-menu-item"
              onClick={() => {
                onOpenHelp();
                onClose();
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#111827',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Help
            </button>
          )}

          {onOpenAbout && (
            <button
              className="ov-settings-menu-item"
              onClick={() => {
                onOpenAbout();
                onClose();
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#111827',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M12 8v4"/><path d="M12 16h.01"/>
              </svg>
              About
            </button>
          )}
        </>
      )}

      {showAppItems && showSessionItems && (
        <div style={{ height: '1px', background: '#E5E7EB', margin: '0.5rem 0' }}/>
      )}

      {onClearChat && (
        <button 
          className="ov-settings-menu-item" 
          onClick={() => {
            if (onClearChat) onClearChat();
            onClose();
          }}
          style={{
            width: '100%',
            padding: '0.75rem',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            color: '#111827',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          Clear Chat
        </button>
      )}

      {onShareSession && (
        <button 
          className="ov-settings-menu-item" 
          onClick={() => {
            if (onShareSession) onShareSession();
            onClose();
          }}
          style={{
            width: '100%',
            padding: '0.75rem',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            color: '#111827',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
          Share Session
        </button>
      )}

      {onExportChat && (
        <button 
          className="ov-settings-menu-item" 
          onClick={() => {
            if (onExportChat) onExportChat();
            onClose();
          }}
          style={{
            width: '100%',
            padding: '0.75rem',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            color: '#111827',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Export Chat
        </button>
      )}
    </div>
  );

  // Portal to <body> so it's not affected by widget stacking contexts/transforms.
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(content, document.body);
  }
  return content;
};

export default SettingsMenu;
